<template>
  <div>
    <!-- Header -->
    <div class="glass-card bg-gray-800 text-white p-3 mb-4">
      <h2 class="text-lg font-semibold">Tunnels for {{ site.name }}</h2>
      <p class="text-sm text-gray-300">
        Site Code: {{ site.code }} | Total tunnels: {{ filteredTunnels.length }}
      </p>
    </div>

    <div v-if="tunnelsLoaded">
      <div v-if="filteredTunnels.length">
        <!-- Tunnels Table -->
        <div v-for="tunnel in filteredTunnels" :key="tunnel.id" class="overflow-hidden rounded-xl backdrop-blur-md bg-white/80 dark:bg-slate-900/60 mb-8">
          <div
            class="bg-gray-800 dark:bg-gray-800/30 text-white p-3 rounded-t-md flex justify-between items-center"
          >
            <div>
              <h3 class="text-lg font-medium">
                {{ tunnel.name }}
              </h3>
            </div>
            <span
              :class="[
                'px-3 py-1 rounded-full text-sm font-medium',
                tunnel.pending
                  ? 'bg-yellow-100 text-yellow-800'
                  : 'bg-green-600 text-white font-bold',
              ]"
            >
              {{ tunnel.pending ? "Pending" : "Approved" }}
            </span>
          </div>

          <table class="w-full text-left">
            <thead class="text-white bg-gradient-to-r from-slate-900/95 to-slate-800/95 dark:from-slate-900/95 dark:to-slate-800/95 backdrop-blur-md border-b border-slate-700/50 dark:border-slate-700/50">
              <tr>
                <th class="py-2 px-4">Field</th>
                <th class="py-2 px-4">Original Value</th>
                <th class="py-2 px-4">New Value</th>
              </tr>
            </thead>
            <tbody  class="divide-y dark:divide-gray-600/60">
              <tr
                v-for="(field, index) in tunnelFields"
                :key="field.key"
                :class="[
                  'border-b dark:border-gray-700',
                  isFieldEdited(tunnel.id, field.key)
                    ? 'bg-yellow-50/80 dark:bg-gray-700/60'
                    : index % 2 === 0
                    ? 'hover:bg-gray-900/30 transition-colors'
                    : 'hover:bg-gray-900/30 transition-colors',
                ]"
              >
                <td class="py-4 px-4 font-medium">{{ field.label }}</td>
                <td class="py-4 px-4">
                  {{ formatValue(field.key, tunnel[field.key]) }}
                </td>
                <td class="py-4 px-4">
                  <div class="flex items-center">
                    <template
                      v-if="editingField === tunnel.id + '-' + field.key"
                    >
                      <div class="flex items-start gap-2 w-full">
                        <template v-if="field.type === 'checkbox'">
                          <div class="flex-grow">
                            <input
                              v-model="editValues[tunnel.id][field.key]"
                              @blur="resetIfNotSaved(tunnel.id, field.key)"
                              type="checkbox"
                              class="h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            />
                          </div>
                        </template>
                        <template v-else>
                          <div class="w-full flex-grow">
                            <input
                              v-model="editValues[tunnel.id][field.key]"
                              :type="field.type"
                              @keyup.enter="handleEdit(tunnel.id, field.key)"
                              @blur="resetIfNotSaved(tunnel.id, field.key)"
                              @focus="clearFieldError(key)"
                              :class="[
                                'block w-full px-3 py-2 text-base border focus:outline-none focus:ring-2 sm:text-sm rounded-md',
                                  errors[field.key]
                                  ? 'ring-2 ring-red-500 border-red-500 focus:ring-red-500 focus:border-red-500'
                                  : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                              ]"
                            />
                            <FieldError :message="errors[field.key]" class="mt-1"/>
                          </div>
                        </template>
                        <div class="flex pl-2 space-x-2">
                          <button
                            type="button"
                            @click.stop="handleEdit(tunnel.id, field.key)"
                            @mousedown.prevent
                            class="btn bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            Save
                          </button>
                          <button
                            type="button"
                            @click.stop="cancelEdit(tunnel.id, field.key)"
                            @mousedown.prevent
                            class="btn bg-gray-500 text-white hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    </template>
                    <template v-else>
                      <span v-if="handleNoEdit(field.key)"
                        class="flex-grow cursor-pointer"
                        @click="startEdit(tunnel.id, field.key)"
                      >
                        {{
                          formatValue(
                            field.key,
                            editValues[tunnel.id]?.[field.key]
                          )
                        }}
                      </span>
                      <div class="relative">
                        <button 
                          v-if="handleNoEdit(field.key)"
                          @click="startEdit(tunnel.id, field.key)"
                          class="ml-2 p-1 text-gray-400 hover:text-blue-500 dark:text-blue-500 transition-colors duration-150"
                          @mouseover="showToolTip=true, hoveritem=field.key, hoverId=tunnel.id"
                          @mouseleave="showToolTip=false"
                      >
                        <svg
                          class="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                          />
                        </svg>
                      </button>
                      <span 
                        v-if="showToolTip && hoveritem===field.key && hoverId==tunnel.id "
                        class="absolute bottom-full whitespace-nowrap mb-2 transform -translate-x-3/4 rounded py-1 px-2  dark:shadow-lg dark:bg-gray-500 bg-gray-800 bg-gray-800 text-white text-center"
                          >
                        Change {{field.label}}
                      </span>
                      </div>
                    </template>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <div v-else class="text-center py-8 text-gray-500">
        <p>No tunnels found for this site.</p>
        <p class="text-sm text-gray-400 mt-2">Site Code: {{ site.code }}</p>
      </div>
    </div>
    <div v-else class="flex justify-center items-center p-8">
      <div
        class="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"
      ></div>
      <span class="ml-2">Loading tunnels...</span>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch } from "vue";
import { updateTunnel } from "@/services/tunnelService";
import { useStore } from "@/store";
import { useRoute } from "vue-router";
import FieldError from "../FieldError.vue";

const emit = defineEmits(["update-tunnel"]);
const editingField = ref(null);
const editValues = ref({});
const showToolTip = ref(false);
const noEditFields = ref(['name']);
const canChangeTunnel = ref(true);
const error = ref("");
const errorCode = ref("");

const props = defineProps({
  site: {
    type: Object,
    required: true,
  },
  tunnels: {
    type: Array,
    default() {
      return [];
    },
  },
  tunnelsLoaded: {
    type: Boolean,
    required: true,
  },
  errors: {
    type: Object,
    required: true,
  },
});

const route = useRoute();

const tunnelFields = [
  { key: "name", label: "Name", type: "text" },
  { key: "address", label: "Address", type: "text" },
  { key: "port", label: "Port", type: "number" },
  { key: "primary", label: "Primary", type: "checkbox" },
];

// Initialize editValues whenever tunnels change
watch(
  () => props.tunnels,
  (newTunnels) => {
    const newEditValues = {};
    newTunnels.forEach((tunnel) => {
      newEditValues[tunnel.id] = { ...tunnel };
    });
    editValues.value = newEditValues;
  },
  { immediate: true }
);

watch(
  () => useStore.getUser(),
  async (newUser) => {
    if (newUser) {
      canChangeTunnel.value = await useStore.hasPermission("Can change tunnel");
    }
  },
  { immediate: true }
);

const user = () => {
  return useStore.getUser();
};


const filteredTunnels = computed(() => {
  if (!props.tunnels || !props.site) return [];

  return props.tunnels.filter((tunnel) => {
    const matchesSiteId = tunnel.site_id === props.site.id;
    const matchesSiteCode = tunnel.name.includes(props.site.code);
    return matchesSiteId || matchesSiteCode;
  });
});

const startEdit = (tunnelId, key) => {
  editingField.value = tunnelId + "-" + key;
  showToolTip.value = false
};

const handleEdit = async (tunnelId, key) => {
  try {
    const updatedTunnel = { ...editValues.value[tunnelId], id: tunnelId };

    const is_staff = useStore.isStaff();
    const schemaName = route.query.client;
    if (is_staff && !schemaName) {
      error.value = "Organization schema is required!";
      errorCode.value = "Failed to update tunnel.";
      return;
    }
    await updateTunnel(tunnelId, updatedTunnel, is_staff ? schemaName : null);
    emit("update-tunnel", updatedTunnel);
    editingField.value = null;
    error.value = "";
    errorCode.value = "";

  } catch (err) {
    const errData = err?.response?.data;
    tunnelFields.forEach(element => {
      const fieldError = errData?.[element.key];
      if (fieldError?.[0]) {
        props.errors[element.key] = fieldError[0];
      }
    });
    console.error("Error updating tunnel:", err);
    error.value = "Failed to update tunnel.";
    errorCode.value = err?.response?.status || "Unknown error";
    // Reset to original value on error
    editValues.value[tunnelId][key] = props.tunnels.find(
      (t) => t.id === tunnelId
    )[key];
  }
};

const handleNoEdit = (key) => {
  if(canChangeTunnel.value || user()?.is_staff){
    return !noEditFields.value.includes(key);
  }
};

const isFieldEdited = (tunnelId, key) => {
  const tunnel = props.tunnels.find((t) => t.id === tunnelId);
  return tunnel && editValues.value[tunnelId]?.[key] !== tunnel[key];
};

const formatValue = (key, value) => {
  if (value === undefined || value === null) return "";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return value.toString();
};

const resetIfNotSaved = (tunnelId, key) => {
  setTimeout(() => {
    if (editingField.value === tunnelId + "-" + key) {
      cancelEdit(tunnelId, key);
    }
  }, 200);
};

const cancelEdit = (tunnelId, key) => {
  clearFieldError(key);
  const tunnel = props.tunnels.find((t) => t.id === tunnelId);
  editValues.value[tunnelId][key] = tunnel[key];
  editingField.value = null;
};

const clearFieldError = (fieldName) => {
  if ( props.errors[fieldName]) {
    delete props.errors[fieldName];
    delete props.errors['non_field_errors'];
  }
};
</script>
